// frontend/src/pages/StoryCreatePage.jsx
import React, { useEffect, useRef, useState } from "react";
import "../styles/StoryCreate.css";

export default function StoryCreatePage() {
  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const pressTimer = useRef(null);

  const [stream, setStream] = useState(null);
  const [recording, setRecording] = useState(false);
  const [preview, setPreview] = useState(null);
  const [time, setTime] = useState(0);
  const [facing, setFacing] = useState("user");

  /* TEXT */
  const [text, setText] = useState("");
  const [textColor, setTextColor] = useState("#ffffff");
  const [showText, setShowText] = useState(false);

  /* EFFECTS */
  const [showEffects, setShowEffects] = useState(false);
  const [activeFilter, setActiveFilter] = useState("none");

  const filters = {
    none: "none",
    bw: "grayscale(1)",
    vivid: "contrast(1.2) saturate(1.4)",
    warm: "sepia(0.4) saturate(1.2)",
    cool: "hue-rotate(180deg)",
  };

  /* CAMERA */
  useEffect(() => {
    if (!preview) startCamera();
    return stopCamera;
    // eslint-disable-next-line
  }, [facing, preview]);

  const startCamera = async () => {
    stopCamera();
    const s = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: facing },
      audio: true,
    });
    setStream(s);
    if (videoRef.current) videoRef.current.srcObject = s;
  };

  const stopCamera = () => {
    stream?.getTracks().forEach((t) => t.stop());
  };

  /* TIMER */
  useEffect(() => {
    if (!recording) return;
    const timer = setInterval(() => {
      setTime((t) => (t >= 60 ? 60 : t + 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [recording]);

  /* PHOTO */
  const takePhoto = () => {
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.filter = filters[activeFilter];
    ctx.drawImage(videoRef.current, 0, 0);
    canvas.toBlob((blob) => {
      setPreview({
        type: "photo",
        url: URL.createObjectURL(blob),
        blob,
      });
    }, "image/jpeg");
  };

  /* VIDEO */
  const startVideo = () => {
    if (!stream) return;
    chunksRef.current = [];
    const rec = new MediaRecorder(stream);
    rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setPreview({
        type: "video",
        url: URL.createObjectURL(blob),
        blob,
      });
    };
    rec.start();
    recorderRef.current = rec;
    setRecording(true);
    setTime(0);
  };

  const stopVideo = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  /* TAP / HOLD */
  const onPressStart = () => {
    pressTimer.current = setTimeout(() => {
      startVideo();
      pressTimer.current = null;
    }, 250);
  };

  const onPressEnd = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
      takePhoto();
    } else if (recording) {
      stopVideo();
    }
  };

  /* PREVIEW ACTIONS (LOCAL) */
  const sendPreview = () => {
    // branchement backend plus tard
    setPreview(null);
    setText("");
    setShowText(false);
  };

  return (
    <div className="story-page">
      {/* CAMERA */}
      {!preview && (
        <div className="story-video-wrapper">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={facing === "user" ? "mirror" : ""}
            style={{ filter: filters[activeFilter] }}
          />
        </div>
      )}

      {/* PREVIEW */}
      {preview && (
        <div className="story-video-wrapper">
          {preview.type === "photo" ? (
            <img
              src={preview.url}
              alt=""
              style={{ filter: filters[activeFilter] }}
            />
          ) : (
            <video
              src={preview.url}
              autoPlay
              loop
              playsInline
              style={{ filter: filters[activeFilter] }}
            />
          )}
        </div>
      )}

      {/* TEXT */}
      {showText && (
        <input
          className="story-draggable-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ color: textColor }}
          placeholder="Votre texte"
          autoFocus
        />
      )}

      <div className="story-ui">
        {/* HEADER */}
        <header className="story-header">
          <button className="icon-btn close" onClick={() => window.history.back()}>
            ✕
          </button>
          <div className="music-pill">Ajouter un son</div>
        </header>

        {/* SIDEBAR */}
        <aside className="story-sidebar">
          <div
            className="sidebar-item"
            onClick={() =>
              setFacing((f) => (f === "user" ? "environment" : "user"))
            }
          >
            <div className="icon-circle">🔄</div>
            <span>Flip</span>
          </div>

          <div className="sidebar-item" onClick={() => setShowText(true)}>
            <div className="icon-circle">✏️</div>
            <span>Text</span>
          </div>

          <div className="sidebar-item">
            <input
              type="color"
              className="color-dot"
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
            />
            <span>Color</span>
          </div>

          <div className="sidebar-item" onClick={() => setShowEffects(true)}>
            <div className="icon-circle">🪄</div>
            <span>Effects</span>
          </div>
        </aside>

        {/* FOOTER */}
        <footer className="story-footer">
          {!preview && (
            <div className="controls-main">
              <div
                className={`record-trigger ${
                  recording ? "is-recording" : ""
                }`}
                onMouseDown={onPressStart}
                onMouseUp={onPressEnd}
                onTouchStart={onPressStart}
                onTouchEnd={onPressEnd}
              >
                <svg className="record-progress" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    strokeDasharray="282"
                    strokeDashoffset={282 - (time / 60) * 282}
                  />
                </svg>
                <div className="record-outer">
                  <div className="record-inner"></div>
                </div>
              </div>
            </div>
          )}
        </footer>
      </div>

      {/* PREVIEW ACTIONS */}
      {preview && (
        <div className="preview-actions">
          <button onClick={sendPreview}>Envoyer</button>
          <button className="danger" onClick={() => setPreview(null)}>
            Supprimer
          </button>
        </div>
      )}

      {/* EFFECTS MODAL */}
      {showEffects && (
        <div
          className="music-modal-overlay"
          onClick={() => setShowEffects(false)}
        >
          <div
            className="music-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {Object.keys(filters).map((f) => (
              <div
                key={f}
                className="music-item"
                onClick={() => {
                  setActiveFilter(f);
                  setShowEffects(false);
                }}
              >
                {f}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
/* tt fonctone sa va en bdd */
